/**
 * FormField — a label + arbitrary control + hint + error wrapper for
 * cases where <Input>/<Select>/<Textarea> aren't enough (file pickers,
 * react-select, custom controls, date pickers, etc.).
 *
 *   <FormField label="Avatar" hint="JPG or PNG, under 5MB">
 *     <CustomFileUpload />
 *   </FormField>
 *
 *   <FormField label="Date of birth" required error={errors.dob}>
 *     <DatePicker ... />
 *   </FormField>
 *
 * For plain text inputs use <Input> directly — it already wraps itself
 * in a FormField-equivalent layout.
 */

import React, { useId } from "react";
import Label from "./Label";

interface FormFieldProps {
  /** Label rendered above the control. */
  label?: React.ReactNode;
  /** Visual required marker (the `*`). */
  required?: boolean;
  /** Error text — renders red below the control, suppresses hint. */
  error?: string;
  /** Helper text — renders muted below the control. */
  hint?: string;
  /** Children = the actual control element(s). */
  children: React.ReactNode;
  /** Override the id used by htmlFor — otherwise auto-generated. */
  htmlFor?: string;
  /** Override the outer container className. */
  className?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  error,
  hint,
  children,
  htmlFor,
  className = "",
}) => {
  const fallbackId = useId();
  const id = htmlFor || fallbackId;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
};

export default FormField;
